import { Token } from 'src/types';

import { AppKitNetwork, sepolia } from '@reown/appkit/networks';

export const SHOW_DEFI_PAGE = import.meta.env.VITE_SHOW_DEFI_PAGE === 'true';
export const SHOW_PORTFOLIO_PAGE =
  import.meta.env.VITE_SHOW_PORTFOLIO_PAGE === 'true';

export const FAUCET_API_URL = import.meta.env.VITE_FAUCET_API_URL;
export const SHOW_FAUCET =
  import.meta.env.VITE_SHOW_FAUCET === 'true' && !!FAUCET_API_URL;

export const SHOW_GAS_FEES = import.meta.env.VITE_SHOW_GAS_FEES === 'true';

export const DOCS_LINK = import.meta.env.VITE_DOCS_LINK;

export const ALWAYS_SHOW_STAKING_STATUS_BANNER =
  import.meta.env.VITE_ALWAYS_SHOW_STAKING_STATUS_BANNER === 'true';

export const STATUSPAGE_LINK =
  import.meta.env.VITE_STATUSPAGE_LINK || 'https://apriori.statuspage.io';

export const RECOMMEND_HAHA_WALLET =
  import.meta.env.VITE_RECOMMEND_HAHA_WALLET === 'true';

const MONAD_DEVNET_CHAIN_ID = Number(
  import.meta.env.VITE_MONAD_DEVNET_CHAIN_ID || 20143
);
const MONAD_TESTNET_CHAIN_ID = Number(
  import.meta.env.VITE_MONAD_TESTNET_CHAIN_ID || 10143
);

export const CHAIN_ID = Number(import.meta.env.VITE_CHAIN_ID || sepolia.id);
export const INDEXER_API_URL =
  import.meta.env.VITE_INDEXER_API_URL ||
  'https://liquid-staking-backend-cf4eed5fabe6.herokuapp.com';
export const PRIVY_APP_ID = import.meta.env.VITE_PRIVY_APP_ID;

const STAKING_CONTRACT_ADDRESS = import.meta.env.VITE_STAKING_CONTRACT_ADDRESS;
const NNS_CONTRACT_ADDRESS = import.meta.env.VITE_NNS_CONTRACT_ADDRESS;

let _WORKERS_MAPPING;

try {
  _WORKERS_MAPPING = JSON.parse(import.meta.env.VITE_WORKERS_MAPPING || '{}');
} catch (error) {
  _WORKERS_MAPPING = {};
}

export const WORKERS_MAPPING = _WORKERS_MAPPING;

type ChainAddresses = {
  staking: `0x${string}`;
  nns?: `0x${string}`;
};

const CONTRACT_ADDRESSES_MAPPING: Record<number, ChainAddresses> = {
  [sepolia.id]: {
    // sepolia
    staking: STAKING_CONTRACT_ADDRESS,
  },
  [MONAD_DEVNET_CHAIN_ID]: {
    // Devnet Monad
    staking: STAKING_CONTRACT_ADDRESS,
    nns: NNS_CONTRACT_ADDRESS,
  },
  [MONAD_TESTNET_CHAIN_ID]: {
    // Testnet Monad
    staking: STAKING_CONTRACT_ADDRESS,
    nns: NNS_CONTRACT_ADDRESS,
  },
};

export const CONTRACT_ADDRESSES = CONTRACT_ADDRESSES_MAPPING[CHAIN_ID];

type NetworkVars = {
  explorer: string;
};

const chainPublicUrls: Record<number, string> = {
  [sepolia.id]: sepolia.rpcUrls.default.http[0],
  // [MONAD_DEVNET_CHAIN_ID]: '',
  // [MONAD_TESTNET_CHAIN_ID]: '',
};

const getRpcUrls = (chainId: number) => {
  const publicUrl = chainPublicUrls[chainId];

  return ((import.meta.env.VITE_RPC_URLS as string) || '')
    .split('_._')
    .map((url) => (url === 'PUBLIC' ? publicUrl : url))
    .filter(Boolean);
};

const NETWORK_VARS_MAPPING: Record<number, NetworkVars> = {
  [sepolia.id]: {
    explorer: 'https://sepolia.etherscan.io',
  },
  [MONAD_DEVNET_CHAIN_ID]: {
    explorer: 'https://explorer.monad-devnet.devnet101.com',
  },
  [MONAD_TESTNET_CHAIN_ID]: {
    explorer: 'https://testnet.monadexplorer.com',
  },
};

const NETWORKS_MAPPING: Record<number, AppKitNetwork> = {
  [sepolia.id]: sepolia,
  [MONAD_DEVNET_CHAIN_ID]: {
    id: MONAD_DEVNET_CHAIN_ID,
    name: 'Monad Devnet',
    nativeCurrency: {
      name: 'Monad',
      symbol: 'MON',
      decimals: 18,
    },
    rpcUrls: {
      default: {
        http: getRpcUrls(MONAD_DEVNET_CHAIN_ID),
      },
    },
    blockExplorers: {
      default: {
        name: 'Monad Explorer',
        url: NETWORK_VARS_MAPPING[MONAD_DEVNET_CHAIN_ID].explorer,
        apiUrl: `${NETWORK_VARS_MAPPING[MONAD_DEVNET_CHAIN_ID].explorer}/api`,
      },
    },
  },
  [MONAD_TESTNET_CHAIN_ID]: {
    id: MONAD_TESTNET_CHAIN_ID,
    name: 'Monad Testnet',
    nativeCurrency: {
      name: 'Monad',
      symbol: 'MON',
      decimals: 18,
    },
    rpcUrls: {
      default: {
        http: getRpcUrls(MONAD_TESTNET_CHAIN_ID),
      },
    },
    blockExplorers: {
      default: {
        name: 'Monad Explorer',
        url: NETWORK_VARS_MAPPING[MONAD_TESTNET_CHAIN_ID].explorer,
        apiUrl: `${NETWORK_VARS_MAPPING[MONAD_TESTNET_CHAIN_ID].explorer}/api`,
      },
    },
  },
};

export const NETWORK_VARS = NETWORK_VARS_MAPPING[CHAIN_ID];

export const RPC_URLS = getRpcUrls(CHAIN_ID);

export const NETWORK_INFO = NETWORKS_MAPPING[CHAIN_ID];

export const MAX_DECIMALS_DISPLAY = 4;

const MON_TOKEN: Token = {
  address: '0x9EC6Ff48A69Ff4A30D3F08b7F50C3C72999a324D',
  symbol: 'MON',
  name: 'Monad',
  decimals: 18,
};

export const TOKENS = [MON_TOKEN];

export const GAS_ESTIMATE_MULTIPLIER = 1.05;

export const TERMS_OF_USE_URL =
  'https://www.notion.so/apriorimev/aPriori-Network-Inc-Terms-of-Use-7079e2f8b4a24e6daf52141090fb15ea';

export const PRIVACY_POLICY_URL =
  'https://www.notion.so/apriorimev/aPriori-Network-Inc-Privacy-Policy-9792521a7be44bcb8fb8e850980c4343';

export const HEADER_LINKS = [
  { to: '/', label: 'Stake' },
  ...(SHOW_DEFI_PAGE ? [{ to: '/defi', label: 'DeFi' }] : []),
  { to: '/withdrawals', label: 'Withdrawals' },
  ...(SHOW_PORTFOLIO_PAGE ? [{ to: '/portfolio', label: 'Portfolio' }] : []),
  ...(DOCS_LINK ? [{ to: DOCS_LINK, label: 'Documentation' }] : []),
  ...(SHOW_FAUCET ? [{ to: '/faucet', label: 'Faucet' }] : []),
];
